import { useState } from "react";
import t from "@/lang/lang";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  Check,
  checkRequiresChildren,
  LastMinuteRiskAnalysis,
} from "@/services/backend/rlt/work-orders/types/last-minute-risk-analysis";
import {
  CheckResultItem,
  useRltLMRAConfirmMutation,
} from "@/services/backend/rlt/work-orders/api/lmra-api";
import { useAuth } from "@/shared/lib/authorization/auth-context";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/shared/components/ui/alert";
import { AlertCircle } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { Card, CardContent, CardFooter } from "@/shared/components/ui/card";
import { Label } from "@/shared/components/ui/label";
import { Input } from "@/shared/components/ui/input";
import { InputValidationErrors } from "@/shared/components/errors/input-error-messages";
import { Button } from "@/shared/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/shared/components/ui/radio-group";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { TableCard } from "@/shared/components/tables/table-card";
import { cn } from "@/shared/lib/utils";

interface CheckHandlers {
  setResult: (checkId: string, result: number | null) => void;
  toggleSelected: (checkId: string, index: number, checked: boolean) => void;
  setComment: (checkId: string, comment: string) => void;
}

export function UnconfirmedLMRAComponent(props: {
  lmra: LastMinuteRiskAnalysis;
  disabled?: boolean;
}) {
  const { lmra, disabled = false } = props;
  const [checks, setChecks] = useState<Check[]>(lmra.checks);

  const handlers: CheckHandlers = {
    setResult: (checkId, result) =>
      setChecks((prev) => updateCheckResult(prev, checkId, result)),
    toggleSelected: (checkId, index, checked) =>
      setChecks((prev) => updateCheckSelection(prev, checkId, index, checked)),
    setComment: (checkId, comment) =>
      setChecks((prev) => updateCheckComment(prev, checkId, comment)),
  };

  return (
    <div className="space-y-4">
      <StartBeforeWorkAlert />
      <ConfirmationComponent
        lmraId={lmra.id}
        checks={checks}
        allAnswered={checks.every(wasChecked)}
        disabled={disabled}
      />
      <CheckListComponent
        checks={checks}
        handlers={handlers}
        disabled={disabled}
      />
    </div>
  );
}

function updateCheckResult(
  checks: Check[],
  id: string,
  result: number | null,
): Check[] {
  return checks.map((c) => {
    if (c.id === id) {
      return result === null
        ? { ...c, checked: false, result: 0 }
        : { ...c, checked: true, result };
    }
    if (c.children?.length > 0) {
      return { ...c, children: updateCheckResult(c.children, id, result) };
    }
    return c;
  });
}

function updateCheckSelection(
  checks: Check[],
  id: string,
  index: number,
  checked: boolean,
): Check[] {
  return checks.map((c) => {
    if (c.id === id) {
      const current = c.selected ?? [];
      const selected = checked
        ? [...current, index].sort((a, b) => a - b)
        : current.filter((i) => i !== index);
      return { ...c, checked: selected.length > 0, selected };
    }
    if (c.children?.length > 0) {
      return {
        ...c,
        children: updateCheckSelection(c.children, id, index, checked),
      };
    }
    return c;
  });
}

function updateCheckComment(
  checks: Check[],
  id: string,
  comment: string,
): Check[] {
  return checks.map((c) => {
    if (c.id === id) {
      return { ...c, comment };
    }
    if (c.children?.length > 0) {
      return { ...c, children: updateCheckComment(c.children, id, comment) };
    }
    return c;
  });
}

function wasChecked(check: Check): boolean {
  // Multiple-choice checks are optional; leaving them empty has no consequence.
  if (check.multiple) return true;
  if (!check.checked) return false;
  if (
    check.children?.length > 0 &&
    check.childrenIfResultOneOf?.includes(check.result)
  ) {
    return check.children.every(wasChecked);
  }
  return true;
}

function collectAnsweredResults(checks: Check[]): CheckResultItem[] {
  return checks.flatMap((c) => {
    let own: CheckResultItem[] = [];
    if (c.multiple) {
      const selected = c.selected ?? [];
      const comment = c.withComment ? (c.comment ?? "") : "";
      if (selected.length > 0 || comment !== "") {
        own = [
          {
            checkId: c.id,
            selected,
            comment: comment !== "" ? comment : undefined,
          },
        ];
      }
    } else if (c.checked) {
      own = [{ checkId: c.id, result: c.result }];
    }
    const fromChildren = c.children?.length
      ? collectAnsweredResults(c.children)
      : [];
    return [...own, ...fromChildren];
  });
}

function StartBeforeWorkAlert() {
  return (
    <Alert>
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>{t("Vor Beginn durchführen!")}</AlertTitle>
      <AlertDescription>
        {t(
          "Die Gefährdungsbeurteilung / LMRA muss direkt vor Beginn der Arbeiten am Arbeitsplatz durchgeführt werden.",
        )}
      </AlertDescription>
    </Alert>
  );
}

function ConfirmationComponent(props: {
  lmraId: string;
  checks: Check[];
  allAnswered: boolean;
  disabled: boolean;
}) {
  const { lmraId, checks, allAnswered, disabled } = props;
  const { user } = useAuth();
  const [conductedBy, setConductedBy] = useState("");
  const [confirm, { isLoading, error }] = useRltLMRAConfirmMutation();

  const onClick = () => {
    if (isLoading) {
      return;
    }
    confirm({
      id: lmraId,
      conductedBy,
      confirmedBy: user?.id ?? "",
      checkResults: collectAnsweredResults(checks),
    });
  };

  return (
    <Card>
      <CardContent>
        <div className="grid items-center gap-1.5">
          <Label>{t("Durchgeführt von")}</Label>
          <Input
            value={conductedBy}
            onChange={(e) => setConductedBy(e.target.value)}
            placeholder={t("Mitarbeiter")}
            disabled={disabled}
          />
          <InputValidationErrors error={error} field="conductedBy" />
        </div>
        {!allAnswered && (
          <p className="text-muted-foreground mt-2 text-sm">
            {t(
              "Bitte beantworten Sie alle Fragen, bevor Sie die Durchführung bestätigen.",
            )}
          </p>
        )}
        <RTKQueryErrorAlert error={error} className="mt-4" />
      </CardContent>
      <CardFooter className="flex justify-end">
        <Button
          variant="outline"
          className="bg-white"
          onClick={onClick}
          disabled={isLoading || !allAnswered || disabled}
        >
          {t("Durchführung bestätigen")}
        </Button>
      </CardFooter>
    </Card>
  );
}

function CheckListComponent(props: {
  checks: Check[];
  handlers: CheckHandlers;
  disabled: boolean;
}) {
  const { checks, handlers, disabled } = props;

  return (
    <TableCard>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("Nr")}</TableHead>
            <TableHead className="w-full">{t("Fragestellung")}</TableHead>
            <TableHead>{t("Prüfung")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {[...checks].sort(byNumber).map((check) => (
            <CheckComponent
              key={check.id}
              check={check}
              handlers={handlers}
              disabled={disabled}
            />
          ))}
        </TableBody>
      </Table>
    </TableCard>
  );
}

function byNumber(a: Check, b: Check) {
  return a.number - b.number;
}

function CheckComponent(props: {
  check: Check;
  handlers: CheckHandlers;
  disabled: boolean;
}) {
  const { check, handlers, disabled } = props;

  const requiresChildren = checkRequiresChildren(check);

  return (
    <>
      <TableRow className={cn(requiresChildren && "border-b-0")}>
        <TableCell className="align-top" rowSpan={requiresChildren ? 2 : 1}>
          {check.number}
        </TableCell>
        <TableCell className="align-top whitespace-normal">
          {check.description}
        </TableCell>
        <TableCell className="align-top" rowSpan={requiresChildren ? 2 : 1}>
          {check.multiple ? (
            <MultiSelectAnswer
              check={check}
              handlers={handlers}
              disabled={disabled}
            />
          ) : (
            <SingleSelectAnswer
              check={check}
              handlers={handlers}
              disabled={disabled}
            />
          )}
        </TableCell>
      </TableRow>
      {checkRequiresChildren(check) && (
        <ChildCheckListComponent
          checks={check.children}
          handlers={handlers}
          disabled={disabled}
        />
      )}
    </>
  );
}

function ChildCheckListComponent(props: {
  checks: Check[];
  handlers: CheckHandlers;
  disabled: boolean;
}) {
  const { checks, handlers, disabled } = props;

  return (
    <>
      {[...checks].sort(byNumber).map((check) => (
        <ChildCheckComponent
          key={check.id}
          check={check}
          handlers={handlers}
          disabled={disabled}
        />
      ))}
    </>
  );
}

function ChildCheckComponent({
  check,
  handlers,
  disabled,
}: {
  check: Check;
  handlers: CheckHandlers;
  disabled: boolean;
}) {
  return (
    <TableRow>
      <TableCell className="align-top whitespace-normal">
        <div className="mb-4">
          <span className="mr-4">{check.number}</span>
          {check.description}
        </div>
        {check.multiple ? (
          <MultiSelectAnswer
            check={check}
            handlers={handlers}
            disabled={disabled}
          />
        ) : (
          <SingleSelectAnswer
            check={check}
            handlers={handlers}
            disabled={disabled}
          />
        )}
      </TableCell>
    </TableRow>
  );
}

function SingleSelectAnswer(props: {
  check: Check;
  handlers: CheckHandlers;
  disabled: boolean;
}) {
  const { check, handlers, disabled } = props;

  return (
    <RadioGroup
      value={check.checked ? check.options[check.result] : ""}
      onValueChange={(value) => {
        if (value === "") {
          handlers.setResult(check.id, null);
        } else {
          handlers.setResult(check.id, check.options.indexOf(value));
        }
      }}
      disabled={disabled}
    >
      {check.options.map((option) => (
        <Label
          key={option}
          htmlFor={check.id + option}
          className="h-8 cursor-pointer rounded-md border px-2"
        >
          <RadioGroupItem value={option} id={check.id + option} />
          <span>{option}</span>
        </Label>
      ))}
    </RadioGroup>
  );
}

function MultiSelectAnswer(props: {
  check: Check;
  handlers: CheckHandlers;
  disabled: boolean;
}) {
  const { check, handlers, disabled } = props;
  const selected = check.selected ?? [];

  return (
    <div className="flex min-w-64 flex-col gap-2.5">
      {check.options.map((option, index) => (
        <div key={`${check.id}-${index}`} className="flex items-start gap-2">
          <Checkbox
            id={`${check.id}-${index}`}
            className="mt-0.5"
            checked={selected.includes(index)}
            onCheckedChange={(value) =>
              handlers.toggleSelected(check.id, index, value === true)
            }
            disabled={disabled}
          />
          <Label
            htmlFor={`${check.id}-${index}`}
            className="cursor-pointer text-sm leading-snug font-normal"
          >
            {option}
          </Label>
        </div>
      ))}
      {check.withComment && (
        <Input
          value={check.comment ?? ""}
          onChange={(e) => handlers.setComment(check.id, e.target.value)}
          placeholder={t("Freitext")}
          disabled={disabled}
        />
      )}
    </div>
  );
}
